import { BOT_NAMES } from '@big2/shared';
import type { ClientState } from './socket/types.js';

const englishByMongolian = new Map(
  BOT_NAMES.map(({ name, englishName }) => [`Bot ${name}`, `Bot ${englishName}`]),
);
const englishCache = new WeakMap<object, object>();

function cachedEnglish<T extends object>(source: T, translate: () => T): T {
  const cached = englishCache.get(source);
  if (cached) return cached as T;
  const translated = translate();
  englishCache.set(source, translated);
  return translated;
}

function englishBotName(name: string, isBot: boolean): string {
  return isBot ? englishByMongolian.get(name) ?? name : name;
}

export function localizeBotNames(state: ClientState, language: 'mn' | 'en'): ClientState {
  if (language === 'mn') return state;

  const isBotId = (playerId: string) => playerId.startsWith('bot-');
  const { lobby, game, roomHistory } = state;
  return {
    ...state,
    lobby: lobby && cachedEnglish(lobby, () => ({
      ...lobby,
      players: lobby.players.map(player => ({
        ...player,
        name: englishBotName(player.name, player.isBot),
      })),
    })),
    game: game && cachedEnglish(game, () => ({
      ...game,
      players: game.players.map(player => ({
        ...player,
        name: englishBotName(player.name, player.isBot),
      })),
    })),
    roomHistory: roomHistory && cachedEnglish(roomHistory, () => ({
      ...roomHistory,
      games: roomHistory.games.map(game => ({
        ...game,
        players: game.players.map(player => ({
          ...player,
          name: englishBotName(player.name, player.isBot),
        })),
      })),
    })),
    reactions: cachedEnglish(state.reactions, () => Object.fromEntries(Object.entries(state.reactions).map(([playerId, reaction]) => [
      playerId,
      { ...reaction, playerName: englishBotName(reaction.playerName, isBotId(playerId)) },
    ]))),
    tableThrows: cachedEnglish(state.tableThrows, () => state.tableThrows.map(throwEvent => ({
      ...throwEvent,
      fromPlayerName: englishBotName(throwEvent.fromPlayerName, isBotId(throwEvent.fromPlayerId)),
      toPlayerName: englishBotName(throwEvent.toPlayerName, isBotId(throwEvent.toPlayerId)),
    }))),
    voiceTaunts: cachedEnglish(state.voiceTaunts, () => Object.fromEntries(Object.entries(state.voiceTaunts).map(([key, taunt]) => [
      key,
      { ...taunt, fromPlayerName: englishBotName(taunt.fromPlayerName, isBotId(taunt.fromPlayerId)) },
    ]))),
  };
}
